<template>
  <div class="project-page">
    <!-- 圖片預加載組件 -->
    <ImagePreloader 
      :images="allProjectImages"
      @preload-complete="handleImagePreloadComplete"
      @progress-update="handleProgressUpdate"
    />
    <!-- 返回按钮 -->
    <div class="back-button" @click="goToIntroduction">
      &#8592;
    </div>
    
    <!-- A-L 页面导航键 -->
    <div class="alphabet-navigation">
      <router-link 
        v-for="(letter, index) in alphabetKeys" 
        :key="letter"
        :to="`/project/${projectIds[index]}`"
        :class="['alphabet-key', projectId === projectIds[index] ? 'active' : '']"
      >
        {{ letter }}
      </router-link>
    </div>
    
    <!-- 导航按钮 -->
    <div class="navigation-buttons">
      <router-link :to="prevProjectLink" class="nav-button prev" v-if="prevProjectId !== null">
        <span class="arrow">←</span>
        <span class="text">Previous</span>
      </router-link>
      <router-link :to="nextProjectLink" class="nav-button next" v-if="nextProjectId !== null">
        <span class="text">Next</span>
        <span class="arrow">→</span>
      </router-link>
    </div>

    <div class="project-content">
      <!-- 图片轮播 -->
      <div class="image-carousel">
        <transition-group name="fade">
          <div 
            v-for="(image, index) in projectImages" 
            :key="index"
            v-show="currentImageIndex === index"
            class="carousel-image"
            :style="{ backgroundImage: `url(${image})` }"
          ></div>
        </transition-group>
        
        <!-- 轮播指示器 -->
        <div class="carousel-indicators">
          <span 
            v-for="(_, index) in projectImages" 
            :key="index"
            :class="{ active: currentImageIndex === index }"
            @click="currentImageIndex = index"
          ></span>
        </div>
      </div>

      <!-- 项目信息 -->
      <div class="project-info">
        <h1>{{ language === 'zh' ? project.title : project.titleEn }}</h1>
        <div class="project-artist">{{ project.artist }}</div>
        
        <!-- 语言切换按钮 -->
        <div class="language-toggle">
          <button 
            @click="language = 'zh'" 
            :class="{ active: language === 'zh' }"
          >中文</button>
          <button 
            @click="language = 'en'" 
            :class="{ active: language === 'en' }"
          >English</button>
        </div>
        
        <div class="project-description" v-html="formatDescription(language === 'zh' ? project.description : project.descriptionEn)"></div>
      </div>
    </div>
  </div>
</template>

<script>
import ImagePreloader from '@/components/ImagePreloader.vue'

export default {
  name: 'ProjectPage',
  components: {
    ImagePreloader
  },
  data() {
    return {
      imagePreloadComplete: false,
      currentImageIndex: 0,
      intervalId: null,
      language: 'zh', // 默认语言为中文
      alphabetKeys: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'],
      projectIds: [6, 2, 5, 3, 11, 10, 8, 1, 4, 7, 0, 9],
      projects: [
        { 
          id: 6, 
          title: '（ooo）', 
          titleEn: '（ooo）', 
          artist: '周真臻、吳梓瑄、吳宜璇',
          description: '在以人物為中心的世界觀中，「我」透過自身的認知觀察並决策一切。然而，人的選擇實則受體内億萬微生物影響一它們不斷向大腦傳遞訊號，塑造行為，共同構築完整的「我」。\n\n當這些微生物被提取出來，成為獨立的「物」，它們擁有自身的成長軌跡，甚至能透過培養皿與外部環境互動。這正體現了物導向本體論的核心思想一微生物不僅是人體的附屬，而是擁有獨立「發聲」能力的存在，正是它們的組合，造就了「我」。\n\n當觀眾將培養皿置於播放器上，他們能夠「聆聽」微生物當下的状態，試圖理解它們如何與環境對話。\n\n而作品名稱《(。。。)》，是取自作品的核心思想OOO（物導向本體論），同時也想表蓬一組開放的符號，沒有既定的語意，讓人聯想到微生物的神秘性與不可預測性。',
          descriptionEn: 'In a worldview centered on the human individual, "I" perceive and make decisions based on my own cognition. Yet human choices are, in reality, shaped by billions of microorganisms within the body—constantly transmitting signals to the brain, influencing behavior, and collectively constructing the complete "self."\n\nWhen these microorganisms are extracted and become independent "objects," they embark on their own trajectories of growth, even interacting with the external environment through petri dishes. This embodies the core tenet of Object-Oriented Ontology (OOO)—microorganisms are not mere appendages of the human body but autonomous entities capable of "speaking." It is their assemblage that constitutes "me."\n\nBy placing the petri dish onto the device, the audience can "listen" to the current state of the microorganisms, attempting to decipher how they converse with their surroundings.\n\nThe title of the work, （ooo）, draws from the central concept of OOO (Object-Oriented Ontology), while also serving as an open-ended symbol—devoid of fixed meaning—evoking the mystery and unpredictability of microbial life.',
          images: [
            require('@/assets/images/projects/project7.jpg'),
            require('@/assets/images/projects/project7-2.jpg'),
            require('@/assets/images/projects/project7-3.jpg')
          ]
        },
        { 
          id: 2, 
          title: '在呼',
          titleEn: 'Childhood', 
          artist: '莊涵韻、林亞萱、黃翊庭',
          description: '隨著年齡的增長，我們似乎逐漸失去了那份探索世界的熱情。回想兒時，一顆單純的泡泡就能帶給我們無盡的驚喜與歡樂。追逐泡泡的瞬間，時間像被按下了暫停鍵，那樣純粹的快樂成為童年記憶中最溫暖的泉源。然而現實生活的責任與壓力如影隨形，我們漸漸忘了那份不帶功利的喜悅，也遺失了那顆無所畏懼、對萬物充滿好奇的初心。\n\n我們希望透過這件作品，帶領觀眾穿越時光，回到無憂無慮的過去。「在呼」是一把鑰匙，能夠輕輕叩開每個人心底塵封已久的門扉，喚醒那份沉睡已久、對世界的好奇與熱情。或許在這一刻，我們能再次邂逅生活中微小的美好——如同那些在陽光下閃爍、飄浮的泡泡，重新找回對生命無限可能的期待與感動。',
          descriptionEn: 'As time quietly passes, we seem to drift away from the curiosity and wonder that once lit up our world. There was a time when even a single bubble could spark boundless joy and excitement. In those moments of chasing bubbles, it felt as if time stood still—pure, unfiltered happiness that became a treasured part of our childhood memories. Yet as life gradually fills with responsibilities and unspoken pressures, we begin to forget the joy that comes without purpose, and lose the fearless, curious heart we once had.\n\nThrough this piece, we hope to guide the audience on a journey through time, back to those carefree days. "Childhood" becomes a key—a gentle knock on the long-forgotten doors of our hearts, awakening that dormant sense of wonder and passion for the world. Perhaps, in this very moment, we can once again notice the small, beautiful things in life—like bubbles drifting and shimmering in the sunlight—and rediscover the sense of awe and endless possibility that once filled our hearts.',
          images: [
            require('@/assets/images/projects/project3.jpg'),
            require('@/assets/images/projects/project3-2.jpg'),
            require('@/assets/images/projects/project3-3.jpg')
          ]
        },
        { 
          id: 5, 
          title: '無色體',
          titleEn: ' Achromosome',
          artist: '吳苡婕、葉承昀',
          description: '「什麼時候交男朋友？」「你要適當表現需要幫助的樣子，讓男生覺得他被需要」「你看起來很中性」\n\n國小，男生座號在前半女生座號在後半，男生運動服是藍色女生是粉紅色，座位組合要一男一女，掃地工作男生抬餐桶女生擦桌子……還有數數種種，用男女作遊戲規則的安排、制度，打從進入第一個社會化機構開始就這麼的順其自然，是的，一切都這麼自然。因為我們的生理構造不一樣—因為你有陰莖我有陰道？因為你有睪固酮？因為你有Y？因為我們有子宮？直到新研究發現有子宮有Y染色體並非不可能共存，沒有Y但有陽具也並非不可能共存，基因可以突變，我們是人，不是機器。所以我們迷惘了，怎麼樣是真的女生？誰說的算？誰可以當女生？你是女生？你確定嗎？\n\n謝謝茱蒂絲．巴特勒（Judith butler）—這件作品的老師之一，從性、性別、色情、強暴、社會、人類歷史、女性、身體等等激勵我們思考這場「展演」的宗旨、架構、角色。\n\n喔對，我們只是想換個存在的形狀，不是你們想的那個名為出櫃的行為（因為有老師問），就是這部戲我們演膩了。',
          descriptionEn: '"When are you getting a boyfriend?"\n"You need to show that you need help, so men will feel needed."\n"You look kind of neutral."\n\nIn elementary school, boys had lower seat numbers, girls had higher ones. Boys wore blue PE uniforms, girls wore pink. Desks were arranged boy-girl-boy-girl. During cleanup duty, boys carried lunch buckets, girls wiped desks… Countless rules, games, systems — all founded on this binary. From the very first institution of socialization, everything felt "natural." Yes, it was all so natural. Because our biological structures are different — Because you have a penis and I have a vagina? Because you have testosterone? Because you have a Y chromosome? Because we have a uterus?\n\nBut new studies show that having a uterus and a Y chromosome is not impossible, that not having a Y but having a penis isn\'t either. Genes mutate. We are human, not machines.\n\nSo we are confused. What does it really mean to be a girl? Who gets to decide that? Who can be a girl? Are you a girl? Are you sure?\n\nThank you, Judith Butler — one of the teachers of this work — for urging us to question the very structure, roles, and purpose of this "performance," through your reflections on sex, gender, pornography, rape, society, human history, women, and the body.\n\nOh right! We\'re just trying to find another way of existing. It\'s not what you think. It\'s not some act of "coming out" (because, yes, a professor asked). We\'re just tired of this script. We\'re done playing this role.',
          images: [
            require('@/assets/images/projects/project6.jpg'),
            require('@/assets/images/projects/project6-2.jpg'),
            require('@/assets/images/projects/project6-3.jpg')
          ]
        },
        { 
          id: 3,
          title: 'Pān-Toh',
          titleEn: 'Pān-Toh',
          artist: '詹登凱、黃安榕、邱英瑛',
          description: '辦桌，是屬於我們臺灣人的共同核心經驗，不論婚喪喜慶，都是適合辦桌的場合。而我們對於各種辦桌的共同印象，不僅僅是那道櫻花蝦米糕、抑或是佛跳牆，而是隱隱流動於各個賓客中的「互相謙讓」，上菜後第一位夾菜的人，飲酒時的敬酒、乾杯，甚至於聚餐結尾的打包，全都能從其中看出一個人在此次場合中的身分地位以及人際關係，一般來說，人們也能在大部分辦桌的場合替自己經營形象和打理人際關係，你在用餐當中的一舉一動都會成為周圍人對你的印象評分，於辦桌場合的特定暗號更是數不勝數。我們發覺到這個現象深根於臺灣人的思想中，幾乎所有人都明白這個「不需言語便能意會」的規矩且相當遵守，我們希望能深度挖掘這項特別的「辦桌文化」並將它「白話」地表現出來。\n\n作品以混合實境的形式來進行辦桌情境模擬並邀請觀眾進入遊玩。將餐桌上的微妙禮儀轉化為計算遊戲分數的數值，並設計「餓死、醉死、社死」結局來強調規則所帶來的無形壓力，以幽默與誇張的方式去重現看似如常卻藏滿細節的社交文化。玩家會在適應與各種抉擇中，體驗這套隱隱流動於空間中、不需明說的文化默契。',
          descriptionEn: 'The pān-toh, or traditional Taiwanese banquet, is a shared cultural experience deeply woven into Taiwanese society. Whether celebrating or mourning, these gatherings are marked not only by signature dishes like sakura shrimp rice or Buddha Jumps Over the Wall, but by an intricate web of unspoken social codes. From who takes the first bite to the order of toasts and the ritual of packing leftovers, every gesture reflects status, relationships, and social awareness.\n\nAt the pān-toh, individuals manage their image through silent cues. These subtle performances—understood intuitively by most—reveal the deeply ingrained, wordless rules that shape group dynamics. Our project explores this cultural script by transforming it into an interactive experience.\n\nUsing mixed reality, we simulate a pān-toh setting where players navigate social expectations translated into game mechanics. Seemingly trivial actions affect their score, leading to humorous but telling outcomes such as dying of hunger, dying of intoxication, or dying of social shame. Through this exaggerated lens, we expose the silent pressures behind everyday politeness.\n\nParticipants must adapt and choose carefully, gradually uncovering the invisible etiquette that defines the pān-toh—a space where meaning flows not through speech, but through shared, unspoken understanding.',
          images: [
            require('@/assets/images/projects/project4.jpg'),
            require('@/assets/images/projects/project4-2.jpg'),
            require('@/assets/images/projects/project4-3.jpg')
          ]
        },
        { 
          id: 11, 
          title: '予海', 
          titleEn:'Effuse to Ocean',
          artist: '陳冠儒、黃壬煜',
          description: '與缺席對象的傾吐，其實和看海的時候一樣，都是與自己意識的對話。我們以「看海」的情境與感受為出發點。閉上眼睛想像，一個人靜靜地坐在海邊，吹著海風、感受著浪花的拍打、聽著海浪湧動的聲音。在此時此刻，一切只剩下這片海與自己，所有傾吐都會被承接。對我們而言，海，彷彿乘載了所有思緒，如此的感受使人平靜而安心。\n\n而「寫信」也與「看海」相似，是一種與缺席對象的傾吐，實則也是與自我意識的對話。提筆時，腦中的所感所思凝結成筆下的文字，文字也許不全然精準，但此時的意識卻是波動起伏，無法被掩蓋的。\n\n因此我們融合「寫信」的手法與「看海」的意象，以寫信的方式引發觀眾，在與對象傾吐的同時，也是在與自己對話。最後以海的意向，轉譯觀眾傾吐與對話時的思緒和字裡行間的情感。利用視覺與聽覺的營造，使觀眾彷彿身處自己的腦內世界中，以抽象又具現化的方式與自己的意識對話。',
          descriptionEn: 'Imagine quietly gazing at the ocean, feeling as though nothing remains but the sea and oneself. At this moment, everything you release is gently received by the ocean. To us, the sea seems to bear the weight of all thoughts. Although the chaos within may not always take the shape of words, through the slow ritual of letter writing, it may reach form and clarity. Both writing letters and watching the sea become modes of silent dialogue, communicating with those who are absent, and perhaps with absence itself. This work juxtaposes these two intimate forms of communication. Through an interplay of visual and sound, it invites the viewer into a reflective space. A place where inner thoughts may surface, and a quiet conversation with one\'s self can begin.',
          images: [
            require('@/assets/images/projects/project12.jpg'),
            require('@/assets/images/projects/project12-2.jpg'),
            require('@/assets/images/projects/project12-3.jpg')
          ]
        },
        { 
          id: 10, 
          title: '01110100 01101111', 
          titleEn:'01110100 01101111',
          artist: '賴榆方、許煒晟',
          description: '我們曾獲得一件豪華朗機工淘汰的燈具，據說可以用 Arduino 控制。但當著手研究時才發現，豪華朗機工自行開發板子與程式碼，存儲程式碼的記憶卡卻因為年代久遠毀損，其控制與開關功能盡數喪失，僅剩下能通電的電路板。這使我們注意到，數位資料可能因為各種原因，如電磁干擾或靜電放電而遺失程式碼。從此觀察出發，我們整理出人類，在高度仰賴數位科技的文明發展中可能面臨的三件事：\n\n一、 當代的所有知識、學術、文化發展，已經數位化與雲端化，人類滅絕的那天，這些資產都將一同陪葬。用考古偏差去想像，對未來的考古學來說，人類可能只是一群創造一堆鐵板的猴子。我們可能會成為未來人的史前人類，無從可考的智慧資產。\n\n二、 當代的數位處理器，奠基在中央處理器（CPU）的運作邏輯上，成就了計算機科技的發展，但當這些作業系統、編譯邏輯、解碼邏輯同數位化資產一同消失時。即使未來的考古學家們握著一個 MP4 影像檔案，也無力再窺看這些內容了。\n\n三、 人類花費一整個世紀（如果從智人就開始算的話是好幾萬年），發展出二進制的數位邏輯，也是當代代表的思考邏輯，將會與人類一同陪葬。\n\n《01110100 01101111》從媒體考古的角度出發，試圖傳達數位文化資產與當代電腦邏輯中的數位層與文化層（computer layer and culture layer）。同時，透過科技形式的重新運用，讓我們重新認識當代科技。',
          descriptionEn: 'We once acquired a light fixture that had been discarded by LuxuryLogico, which was said to be controllable via Arduino. However, upon trying out, we discovered that the previous owner had developed their own custom circuit board and code. Unfortunately, the memory card that stored the code might have been damaged over time, rendering all control and switching functions inoperative, leaving only a circuit board capable of powering on. This led us to reflect on how digital data can be lost due to various causes, such as electromagnetic interference or electrostatic discharge. From this observation, we identified three potential challenges facing humanity in an age highly dependent on digital technology:\n\nAll contemporary knowledge, academic research, and cultural development have been digitized and moved to the cloud. On the day humanity becomes extinct, these assets will vanish along with us. From an archaeological perspective, this raises the possibility that future civilizations may only see us as apes who created a lot of metal plates. Our intellectual legacy may become inaccessible, rendering us the prehistoric people of the future.\n\nModern digital processors are built upon the logic of central processing units (CPUs), which have propelled the development of computer technology. However, when operating systems, compiler logic, and decoding mechanisms disappear alongside digital assets, even if future archaeologists obtain an MP4 video file, they may be unable to access its contents.\n\nHumanity has spent an entire century (or tens of thousands of years if we trace back to Homo sapiens) developing binary digital logic—a logic that defines contemporary thinking. Yet this, too, will be buried with humanity.\n\n01110100 01101111 adopts a media archaeology perspective to explore the digital and cultural layers embedded in digital cultural assets and contemporary computer logic. At the same time, through the repurposing of technological forms, we seek to re-examine and re-engage with modern technologies.',
          images: [
            require('@/assets/images/projects/project11.jpg'),
            require('@/assets/images/projects/project11-2.jpg'),
            require('@/assets/images/projects/project11-3.jpg')
          ]
        },
        { 
          id: 8, 
          title: '氣氛大師', 
          titleEn: 'NoPhotoSayG8',
          artist: '林展毅、陳庚宇、陳威羽',
          description: '網路迷因作為數位時代的視覺語言，已超越娛樂用途，成為獨特的文化載體與溝通模式。本作品探索迷因梗圖作為社交工具的可能性，並將其轉化為互動遊戲體驗。本作品鼓勵玩家透過迷因創造即興視覺對話，擺脫日常社交拘謹。我們希望讓參與者體驗並思考：迷因梗圖是否能成為溝通的媒介？《氣氛大師》不僅是一款遊戲，更是科技、藝術與社交文化的實驗。',
          descriptionEn: 'Internet memes, as a visual language of the digital age, have transcended their entertainment function to become unique cultural carriers and modes of communication. This project, The Vibe Master, explores the potential of memes as social tools and transforms them into an interactive gaming experience. By using AI to detect shifts in the "vibe," the game not only enhances fairness and interactivity, but also highlights the potential and limitations of AI in understanding humor, emotion, and social behavior. The AI analyzes the images selected by players, simulating human interpretation of meme contexts, making the gameplay more challenging.',
          images: [
            require('@/assets/images/projects/project9.jpg'),
            require('@/assets/images/projects/project9-2.jpg'),
            require('@/assets/images/projects/project9-3.jpg')
          ]
        },
        { 
          id: 1,
          title: '完美的代價',
          titleEn: 'The Price of Perfection',
          artist: '楊宗穎、彭鈺婷',
          description: '過往的人生總在既定的道路上前行，不必擔憂，只須遵守。然而，當我擁有選擇權了，內心卻茫然失措，不知道該走哪條路？會導向什麼結果？別人又會怎麼看我？種種的擔憂，使我裹足不前。\n\n我們發現，對於未來的迷茫，往往源於對選擇的恐懼——更準確地說，是害怕失敗。隨後發現不只我們懷有這種煩惱，而是許多臺灣學生共同的焦慮。研究顯示，臺灣學生害怕失敗的程度居世界之冠。這讓我們開始反思，在這個高度重視教育與社會期待的環境中，是否隱藏著某些深層問題。\n\n我們透過虛擬實境的形式，隱喻菁英崇拜、文憑主義等壓抑的社會氛圍，映照出學生對於害怕未來失敗的恐懼，並希望藉由互動與理解，使觀眾獲得面對一切的勇氣。\n\n人生，不完美就等於失敗嗎？',
          descriptionEn: 'In the past, life moved steadily along predetermined paths—no need to worry, just follow the rules. But now that I have the freedom to choose, I find myself lost and uncertain. Which path should I take? Where will it lead? What will others think of me? These questions and anxieties keep me from moving forward.\n\nWe came to realize that our confusion about the future often stems from a fear of making choices—or more precisely, a fear of failure. And we\'re not alone in feeling this way. It\'s a common anxiety among many students in Taiwan. Research shows that Taiwanese students have the highest levels of fear of failure in the world. This led us to reflect on whether deeper issues might be hidden beneath an environment that places such a high value on education and societal expectations.\n\nThrough virtual reality, we metaphorically explore the oppressive social atmosphere driven by elitism and credentialism. Our work reflects the fear students feel about the possibility of future failure, and through interaction and understanding, we hope to empower viewers with the courage to confront these fears head-on.\n\nDoes an imperfect life really mean a failed one?',
          images: [
            require('@/assets/images/projects/project2.jpg'),
            require('@/assets/images/projects/project2-2.jpg'),
            require('@/assets/images/projects/project2-3.jpg')
          ]
        },
        { 
          id: 4, 
          title: 'DR↺P', 
          artist: '蔡 荃、李姸蓁',
          description: '現代社會中，許多青少年因層層疊加的學業壓力、同儕競爭及家庭期待不堪重負，將死亡當作逃避現實的終極選擇。然而一旦死亡降臨，就沒有後悔的機會，而所有生命中的微小幸福和未來的潛力也都隨之消逝。我們希望藉此作品呼籲社會大眾正視青少年的心理健康問題，也為那些曾經考慮過死亡的青少年提供一個全新的思考方式，意圖傳遞「生命中依然存在值得珍惜的美好」這一訊息。\n\n這個作品以VR的沉浸式互動體驗呈現，通過模擬各種場景讓觀者親身面對青少年承受的壓力，並隨著故事的進展感受主角在面對生死抉擇時的內心掙扎。當這些壓力逐漸累積，主角開始思考死亡是否是一種解脫。然而，在作品的最終階段，主角回顧了生活中那些微小但充滿溫暖的回憶——從寵物的陪伴到偶然的日常快樂。這些回憶引發了對生命的重新思考，進而意識到即使在絕望中，生命中仍有值得活下去的理由。',
          descriptionEn: 'In today\'s fast-paced society, many teenagers find themselves overwhelmed by mounting pressures—academic performance, social dynamics, and family expectations. For some, this emotional burden can make death seem like the only escape. But once that line is crossed, every fleeting joy and future possibility vanishes—irreversibly.\n\n"DR↺P" invites reflection and offers a new perspective for those who may have felt this way, while also calling on the public to acknowledge the urgent need for youth mental health awareness. Through an immersive, first-person VR experience, viewers step into the world of a teenager wrestling with inner turmoil. As the pressure intensifies, the idea of death surfaces as a form of release. Yet in the final moments, fragments of warmth—small, precious memories—resurface, reigniting a spark of hope. We want to remind viewers that even in darkness, life still holds meaning worth holding on to.',
          images: [
            require('@/assets/images/projects/project5.jpg'),
            require('@/assets/images/projects/project5-2.jpg'),
            require('@/assets/images/projects/project5-3.jpg')
          ]
        },
        { 
          id: 7, 
          title: '心願Yeast', 
          titleEn:'心願Yeast',
          artist: '謝長穎',
          description: '現代人時刻因世界的動盪，心靈精神上的的追求與依賴感變得越來越重要。而我認為，人會透過「去誠心祈福自己的願望」的儀式等方法來堅定心念。不論是寺廟燒香或是求神喝符水種種儀式，都是不同的「沈積」自己對於某件事情的「心願」的方式。\n\n這件作品由三位祈願者，將自己的故事（「面對摯愛的逝去」、「承受身心的極限」與「告別一段心靈羈絆」化為聲音的低語。）訴說給觀眾聽，並邀請觀眾來品嚐祈願者親自製作的「祈願茶」，感受祈願者心願累積的味道。而觀眾將作為祝福者，回饋祝福給祈願者，來完成整個釀造心願的儀式。',
          descriptionEn: 'In today\'s turbulent world, the pursuit and reliance on spiritual nourishment have become increasingly important. I believe that people strengthen their resolve through rituals such as sincerely praying for their wishes. Whether burning incense at temples or drinking talisman water, these various rituals are different ways of \'depositing\' one\'s \'wishes\' for certain matters.\n\nThis artwork features three wish-makers who whisper their stories (\'facing the loss of a loved one,\' \'enduring physical and mental limits,\' and \'leaving a spiritual bond\') to the audience, inviting them to taste the \'Wish Tea\' personally made by the wish-makers and experience the accumulated flavor of their wishes. The audience, acting as blessing-givers, will return blessings to the wish-makers, completing the entire ritual of brewing wishes.',
          images: [
            require('@/assets/images/projects/project8.jpg'),
            require('@/assets/images/projects/project8-2.jpg'),
            require('@/assets/images/projects/project8-3.jpg')
          ]
        },
        { 
          id: 0,
          title: '不會動的手臂',
          titleEn: 'The Deactivated Arm ',
          artist: '劉綵筠、馬渝婷',
          description: '一個科技快速更迭的時代，所有被稱為「最新科技」的東西，其實壽命都沒有真的很長。不管它們曾經多方便，最後都逃不過壞掉、被丟棄的命運，變成地球上的歷史痕跡，甚至垃圾。\n\n我們人類不斷開發、消耗資源，可能早已進入了一個新的地質時代——「人類世」。現在的人類還在瘋狂創新、追求更高科技，但誰知道這個時代會持續多久？在它最巔峰的時候，又會留下什麼？\n\n因此我們創作了一隻機械手臂，機械手臂本來象徵著「曾經運作過」，所以當它停擺壞掉時，指向的是它的過去：一個已經消失的文明，又或者說，一個推測的未來。\n\n我們試著去想像，當人類世走到最巔峰後，會留下什麼樣的東西？這隻無法運作的機械手臂，就是未來的廢棄物，提醒我們：當這一切都過去了，而地球也早已進入下一個地質時代。',
          descriptionEn: 'In an age of rapid technological advancement, so-called "latest technology " innovations often have fleeting lifespans. No matter how transformative they once seemed, these technologies inevitably break down and are discarded—becoming relics of history or, more often, waste.\n\nAs humanity continues to develop and consume, we may already be living in a new geological epoch: the Anthropocene. Today, we persist in our relentless pursuit of progress and higher technologies, yet no one knows how long this era will last—or what will remain when it reaches its peak.\n\nIt is from this reflection that we created a robotic arm. A robotic arm typically signifies labor, function, and purpose. But when it ceases to function, it points not to the future, but to the past: a vanished civilization, or perhaps a speculative future yet to come.\n\nThis non-operational robotic arm serves as a future relic, a monument to the Anthropocene. It stands as a reminder that when all of this ends, the Earth will have already entered the next geological age.',
          images: [
            require('@/assets/images/projects/project1.jpg'),
            require('@/assets/images/projects/project1-2.jpg'),
            require('@/assets/images/projects/project1-3.jpg')
          ]
        },
        { 
          id: 9, 
          title: '沒什麼好想的', 
          titleEn:'Nothing to think About',
          artist: '吳勻楨、何宜樺、黃麗穎',
          description: '我們的發想從探討政治社會、感性思考乃至身體美學,以「即時互動體驗」作為形式。在這次的發想作品中,我們鎖定「觸覺」作為最深刻的知覺感受,打破觀眾與藝術品之間的界線直接與作品的裝置進行觸碰與連結,進而產生共鳴。\n\n本作品透過充氣氣囊壓覆人體,營造出一種異化且親密的「保鮮膜」狀態,將觀眾置於被動受壓的情境中,以探索身體與空間的感知邊界。這樣的裝置不僅在物理層面深刻感受,更在象徵層面揭示了空間政治滲透與社會權力關係的隱喻——控制與被控制,支配與被支配的二元對立。\n\n圍觀者透過鏡頭觀看氣囊下的體驗者,彷彿侵入進這場權力戲碼,並在無意識的情況下參與。反應回饋及觀看行為進一步突顯了權力結構中的不平衡與人性面。該作品旨在挑戰我們對權力動態的認知,探討人類在面對無形約束時的身體與心理回應,並反思個體在集體空間中的主體性與脆弱性。',
          descriptionEn: 'Our project begins with an exploration of political society, emotional perception, and the aesthetics of the body—expressed through the form of real-time interactive experience. In this iteration, we focus on touch as the most profound sensory perception, dissolving the boundary between audience and artwork. Visitors are invited to physically engage with the installation, fostering a direct connection that evokes emotional resonance.\n\nAt the core of the piece is an inflatable airbag structure that presses gently against the body, creating an alienating yet intimate "cling film" state. This experience places participants in a state of passive pressure, prompting a sensory investigation of the borders between body and space. Beyond its physical impact, the work serves as a metaphor for the infiltration of spatial politics and the dynamics of social power—unveiling the dualities of control and submission, dominance and vulnerability.\n\nSpectators observe participants beneath the membrane through a live video feed, unintentionally becoming part of the power play they witness. This act of viewing—and the reactions it elicits—further emphasizes the asymmetry within power structures and the human dimension embedded in them.\n\nThe work challenges conventional understandings of power dynamics, probing the psychological and bodily responses to invisible constraints. It invites reflection on the fragility and agency of the individual within collective space—opening a dialogue between perception, power, and presence.',
          images: [
            require('@/assets/images/projects/project10.jpg'),
            require('@/assets/images/projects/project10-2.jpg'),
            require('@/assets/images/projects/project10-3.jpg')
          ]
        }
      ]
    }
  },
  computed: {
    // 收集所有專案的圖片進行預加載
    allProjectImages() {
      const allImages = [];
      this.projects.forEach(project => {
        if (project.images) {
          allImages.push(...project.images);
        }
      });
      return allImages;
    },
    
    // 获取当前项目ID
    projectId() {
      return parseInt(this.$route.params.id);
    },
    
    // 获取当前项目数据
    project() {
      return this.projects.find(p => p.id === this.projectId) || {};
    },
    
    // 获取当前项目的图片
    projectImages() {
      return this.project.images || [];
    },
    
    // 获取上一个项目ID
    prevProjectId() {
      // 找到当前项目在数组中的索引位置
      const currentIndex = this.projects.findIndex(p => p.id === this.projectId);
      // 如果不是第一个项目，返回前一个项目的ID
      return currentIndex > 0 ? this.projects[currentIndex - 1].id : null;
    },
    
    // 获取下一个项目ID
    nextProjectId() {
      // 找到当前项目在数组中的索引位置
      const currentIndex = this.projects.findIndex(p => p.id === this.projectId);
      // 如果不是最后一个项目，返回后一个项目的ID
      return currentIndex < this.projects.length - 1 ? this.projects[currentIndex + 1].id : null;
    },
    
    // 上一个项目链接
    prevProjectLink() {
      return this.prevProjectId !== null ? `/project/${this.prevProjectId}` : '';
    },
    
    // 下一个项目链接
    nextProjectLink() {
      return this.nextProjectId !== null ? `/project/${this.nextProjectId}` : '';
    }
  },
  methods: {
    // 處理圖片預加載完成
    handleImagePreloadComplete(data) {
      this.imagePreloadComplete = true;
      console.log(`專案頁面圖片預加載完成: ${data.loadedImages}/${data.totalImages}`);
    },
    
    // 處理預加載進度更新
    handleProgressUpdate(progress) {
      // 可以在這裡更新父組件的loading進度
      console.log(`圖片預加載進度: ${progress.toFixed(1)}%`);
    },
    
    // 开始自动轮播
    startAutoSlide() {
      this.intervalId = setInterval(() => {
        this.currentImageIndex = (this.currentImageIndex + 1) % this.projectImages.length;
      }, 3000);
    },
    
    // 停止自动轮播
    stopAutoSlide() {
      clearInterval(this.intervalId);
    },

    // 格式化描述文本，将\n转换为<br>标签
    formatDescription(text) {
      return text ? text.replace(/\n/g, '<br>') : '';
    },

    // 跳转到 IntroductionPage
    goToIntroduction() {
      this.$router.push('/introduction');
    }
  },
  mounted() {
    // 组件挂载后开始自动轮播
    this.startAutoSlide();
  },
  beforeDestroy() {
    // 组件销毁前停止自动轮播
    this.stopAutoSlide();
  },
  watch: {
    // 当路由参数变化时重置轮播索引
    '$route.params.id'() {
      this.currentImageIndex = 0;
      this.stopAutoSlide();
      this.startAutoSlide();
    }
  }
}
</script>

<style scoped>
.project-page {
  min-height: 100vh;
  padding: 60px 20px;
  position: relative;
  background-color: #f8f8f8;
  padding-bottom: 80px; /* 确保内容不被底部导航遮挡 */
}

.project-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
}

/* 图片轮播样式 */
.image-carousel {
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.carousel-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

.carousel-indicators {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
}

.carousel-indicators span {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s;
}

.carousel-indicators span.active {
  background-color: white;
  transform: scale(1.2);
}

/* 项目信息样式 */
.project-info {
  padding: 20px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  text-align: center;
}

.project-info h1 {
  font-size: 2.5rem;
  margin-bottom: 10px;
  color: #F0831E;
}

.project-artist {
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 20px;
}

.project-description {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #444;
  text-align: left;
}

/* 导航按钮样式 */
.navigation-buttons {
  position: fixed;
  top: 50%;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 10;
  pointer-events: none;
  transform: translateY(-50%);
}

.nav-button {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  background-color: rgba(240, 131, 30, 0.5);
  color: white;
  border-radius: 100px;
  text-decoration: none;
  transition: all 0.3s;
  pointer-events: auto;
  box-shadow: 5px 5px 15px rgba(255, 255, 255, 0.8),
              -5px -5px 15px rgba(0, 0, 0, 0.1),
              inset 2px 2px 5px rgba(255, 255, 255, 0.4),
              inset -2px -2px 5px rgba(0, 0, 0, 0.1);
  border: -1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(-5px);
}

.nav-button:hover {
  background-color:  rgba(240, 131, 30, 0.9);
  transform: scale(1.05);
}

.nav-button.prev {
  position: absolute;
  left: 20px;
}

.nav-button.next {
  position: absolute;
  right: 20px;
}

.nav-button .arrow {
  font-size: 1.5rem;
  margin: 0 5px;
}

.nav-button .text {
  font-size: 1rem;
}

/* 过渡动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* 响应式设计 */
@media (min-width: 768px) {
  .project-content {
    flex-direction: column;
  }
  
  .image-carousel {
    width: 100%;
    height: 600px;
  }
  
  .project-info {
    width: 100%;
    max-width: 100%;
  }
  
  /* 桌面版本保持水平佈局 */
  .alphabet-navigation {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
    margin: 0;
    padding: 15px 20px;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    z-index: 100;
    flex-direction: row;
    width: auto;
    height: auto;
    transform: none;
    top: auto;
    border-radius: 0;
    backdrop-filter: none;
    max-height: none;
    overflow-y: visible;
  }
  
  .alphabet-key {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    font-size: 16px;
    background-color: transparent;
    color: #F0831E;
  }
  
  .alphabet-key:hover {
    border: 1px solid #F0831E;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    background-color: transparent;
    color: #F0831E;
  }
  
  .alphabet-key.active {
    background-color: #004D6B;
    color: white;
    box-shadow: 0 0 10px rgba(240, 131, 30, 0.5);
  }
}

@media (max-width: 767px) {
  .navigation-buttons {
    bottom: 20px;
    top: auto;
    transform: none;
  }
  
  .nav-button {
    padding: 10px 15px;
  }
  
  .nav-button.prev {
    left: 20px;
    bottom: 20px;
  }
  
  .nav-button.next {
    right: 20px;
    bottom: 20px;
  }
  
  .image-carousel {
    height: 300px;
  }
  
  /* 移動設備上的A~L導覽按鈕垂直排列，固定在右側 */
  .alphabet-navigation {
    position: fixed !important;
    right: 15px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    left: auto !important;
    bottom: auto !important;
    width: auto !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
    align-items: center !important;
    gap: 8px !important;
    padding: 0 !important;
    background-color: transparent !important;
    border-radius: 0 !important;
    backdrop-filter: none !important;
    box-shadow: none !important;
    max-height: 80vh !important;
    overflow-y: auto !important;
    z-index: 1000 !important;
    margin: 0 !important;
  }
  
  .alphabet-key {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 35px !important;
    height: 35px !important;
    font-size: 14px !important;
    font-weight: bold !important;
    border-radius: 8px !important; /* 保持方格樣式 */
    background-color: rgba(0, 77, 107, 0.8) !important;
    color: white !important;
    margin: 0 !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    text-decoration: none !important;
    transition: all 0.3s ease !important;
  }
  
  .alphabet-key:hover {
    background-color: rgba(240, 131, 30, 0.9) !important;
    color: white !important;
    transform: scale(1.1) !important;
    box-shadow: 0 4px 10px rgba(240, 131, 30, 0.4) !important;
  }
  
  .alphabet-key.active {
    background-color: #F0831E !important;
    color: white !important;
    border: 1px solid white !important;
    box-shadow: 0 4px 10px rgba(240, 131, 30, 0.6) !important;
  }
}

/* 返回按钮样式 */
.back-button {
  position: fixed; /* 改为固定定位 */
  top: 20px;
  left: 20px;
  width: 50px;
  height: 50px;
  background-color: #004D6B;
  color: white;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  font-size: 24px;
  font-weight: bold;
}

.back-button:hover {
  background-color: rgba(240, 131, 30, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4);
}

/* 语言切换按钮样式 */
.language-toggle {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  justify-content: flex-end;
}

.language-toggle button {
  background-color: rgba(255, 255, 255, 0.2);
  color: #004D6B;
  border: none;
  padding: 8px 15px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.language-toggle button:hover {
  background-color: rgba(240, 131, 30, 0.7);
  color: white;
}

.language-toggle button.active {
  background-color: rgba(240, 131, 30, 0.9);
  color: white;
  box-shadow: 0 0 10px rgba(240, 131, 30, 0.5);
}

/* A-L 页面导航键默认样式（桌面版） */
@media (min-width: 768px) {
  .alphabet-navigation {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
    margin: 0;
    padding: 15px 20px;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    z-index: 100;
  }

  .alphabet-key {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px; /* 改为圆角矩形 */
    background-color: transparent; /* 蓝色 */
    color: #F0831E;
    text-decoration: none;
    font-weight: bold;
    transition: all 0.3s ease;
  }

  .alphabet-key:hover {
    border: 1px solid #F0831E; /* 橘色 */
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  .alphabet-key.active {
    background-color: #004D6B; /* 橘色 */
    box-shadow: 0 0 10px rgba(240, 131, 30, 0.5);
  }
}
</style>
